import { auth } from "../services/firebase";
import { writeUserCloudRecord } from "./userDataCloudSync";

const STORAGE_KEY = "manucalc.calculatorResults.v1";
const MAX_RESULTS = 40;
export const CALCULATOR_RESULTS_COLLECTION = "calculatorResults";
export const CALCULATOR_RESULTS_STORAGE_KEY = STORAGE_KEY;
export const CALCULATOR_RESULTS_SYNC_EVENT = "manucalc:calculator-results-updated";

const safeParse = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const getStorage = () => {
  if (typeof window === "undefined") return null;
  return window.localStorage;
};

export const getRecentCalculatorResults = () => {
  const storage = getStorage();
  if (!storage) return [];
  return safeParse(storage.getItem(STORAGE_KEY), []);
};

export const saveCalculatorResult = (result) => {
  const storage = getStorage();
  if (!storage || !result?.calculatorId) return null;

  const entry = {
    id: `${result.calculatorId}-${Date.now()}`,
    timestamp: new Date().toISOString(),
    calculatorId: result.calculatorId,
    calculatorName: result.calculatorName || result.calculatorId,
    inputValues: result.inputValues || {},
    outputValues: result.outputValues || {},
    units: result.units || null,
    material: result.material || null,
    toolData: result.toolData || null,
    operationType: result.operationType || null,
    warnings: result.warnings || [],
    sourceFormulaReferences: result.sourceFormulaReferences || [],
    lineage: result.lineage || [],
  };

  const next = [
    entry,
    ...getRecentCalculatorResults().filter((item) => item.id !== entry.id),
  ].slice(0, MAX_RESULTS);

  storage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(CALCULATOR_RESULTS_SYNC_EVENT, { detail: entry }));
  writeUserCloudRecord(CALCULATOR_RESULTS_COLLECTION, entry, auth.currentUser);
  return entry;
};

const getField = (source, path) =>
  String(path || "")
    .split(".")
    .reduce((value, key) => (value == null ? value : value[key]), source);

const convertLength = (value, fromUnits, toUnits) => {
  if (value == null || value === "") return value;
  if (fromUnits === toUnits) return value;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (fromUnits === "metric" && toUnits === "imperial") return numeric / 25.4;
  if (fromUnits === "imperial" && toUnits === "metric") return numeric * 25.4;
  return value;
};

export const CALCULATOR_FIELD_MAPPINGS = [
  {
    sourceCalculatorId: "feeds-speeds",
    targetCalculatorId: "gcode",
    label: "Feeds & Speeds cutting data",
    confidence: "High",
    fields: [
      { sourceField: "outputValues.rpm", targetField: "spindleRPM", label: "Spindle RPM" },
      { sourceField: "outputValues.machineFeed", targetField: "feedRate", label: "Feed rate" },
      { sourceField: "inputValues.toolDiameter", targetField: "toolDiameter", label: "Tool diameter", unitType: "length" },
      { sourceField: "inputValues.material", targetField: "material", label: "Material" },
      { sourceField: "inputValues.operation", targetField: "sourceOperation", label: "Operation type" },
    ],
  },
  {
    sourceCalculatorId: "bolt-pattern",
    targetCalculatorId: "gcode",
    label: "Bolt pattern coordinates",
    confidence: "High",
    fields: [
      { sourceField: "outputValues.coordinates", targetField: "importedCoordinates", label: "Hole coordinates" },
      { sourceField: "inputValues.holeDiameter", targetField: "toolDiameter", label: "Hole/tool diameter", unitType: "length" },
      { sourceField: "outputValues.holeCount", targetField: "holeCount", label: "Hole count" },
    ],
  },
  {
    sourceCalculatorId: "tap-drill",
    targetCalculatorId: "gcode",
    label: "Tap drill/thread data",
    confidence: "Medium",
    fields: [
      { sourceField: "outputValues.drillSizeDecimal", targetField: "toolDiameter", label: "Tap drill diameter", unitType: "length" },
      { sourceField: "outputValues.pitch", targetField: "threadPitch", label: "Thread pitch" },
    ],
  },
  {
    sourceCalculatorId: "material-selector",
    targetCalculatorId: "feeds-speeds",
    label: "Material selection",
    confidence: "Medium",
    fields: [
      { sourceField: "outputValues.material", targetField: "material", label: "Material" },
    ],
  },
];

export const getCompatibleCalculatorResults = (targetCalculatorId, targetUnits = "imperial") =>
  getRecentCalculatorResults()
    .flatMap((result) => {
      const mappings = CALCULATOR_FIELD_MAPPINGS.filter(
        (mapping) =>
          mapping.targetCalculatorId === targetCalculatorId &&
          mapping.sourceCalculatorId === result.calculatorId
      );

      return mappings.map((mapping) => {
        const sourceUnits = result.units || targetUnits;
        const fields = mapping.fields
          .map((field) => {
            const rawValue = getField(result, field.sourceField);
            const value = field.unitType === "length"
              ? convertLength(rawValue, sourceUnits, targetUnits)
              : rawValue;
            return {
              ...field,
              value,
              sourceUnits,
              targetUnits,
              converted: field.unitType === "length" && sourceUnits !== targetUnits,
            };
          })
          .filter((field) => field.value !== undefined && field.value !== null && field.value !== "");

        return {
          result,
          mapping,
          fields,
          lineage: `${mapping.label} imported from ${result.calculatorName} calculation created ${new Date(result.timestamp).toLocaleString()}.`,
        };
      });
    })
    .filter((candidate) => candidate.fields.length > 0);
